// askAdminForHelp — main-app → sfa-admin subscriber-help handoff.
//
// When a subscriber trips over a real product glitch (broken import,
// parser choke, onboarding snag), we signal the admin panel. Its
// AI employee reads the shape hints and emails the subscriber a
// human-voiced fix, files an engineering bug if warranted, and
// pings the operator on Telegram.
//
// This module is INTENTIONALLY defensive:
//   • Fire-and-forget with an 800 ms AbortController cap. Never blocks
//     the UI, never surfaces its own errors to the caller.
//   • Client-side dedup — a subscriber retrying the same broken upload
//     five times gets ONE email, not five.
//   • PII-guarded — the helper accepts a `shape` object only. It
//     rejects raw file bodies, client rows, phone numbers, emails.
//   • Secret-gated — the server-side proxy at /api/subscriber-help/signal
//     attaches the bearer; the browser never sees SUBSCRIBER_HELP_HANDOFF_SECRET.

const DEDUPE_TTL_MS = 24 * 60 * 60 * 1000; // 24h
const TIMEOUT_MS    = 800;

// UUIDv4 for cross-side correlation with the sfa-admin logs. The
// admin echoes it back in the response + writes it on the incident
// row so either side can grep the same id.
function makeRequestId() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* fall through */ }
  const hex = '0123456789abcdef';
  let out = '';
  for (let i = 0; i < 36; i++) {
    if (i === 8 || i === 13 || i === 18 || i === 23) out += '-';
    else if (i === 14) out += '4';
    else out += hex[Math.floor(Math.random() * 16)];
  }
  return out;
}

// Very small, non-crypto hash for dedup keys. Deterministic across
// tabs so a page reload doesn't multiply the signal.
function shortHash(str) {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h) + str.charCodeAt(i);
  return (h >>> 0).toString(36);
}

function dedupeKey({ tag, owner_id, shape }) {
  const parts = [tag, owner_id || 'anon', JSON.stringify(shape || {})];
  return `sfa:help:sent:${shortHash(parts.join('|'))}`;
}

function shouldSend(key) {
  try {
    const at = Number(localStorage.getItem(key));
    if (!at) return true;
    return (Date.now() - at) > DEDUPE_TTL_MS;
  } catch { return true; }
}

function markSent(key) {
  try { localStorage.setItem(key, String(Date.now())); } catch { /* private mode */ }
}

// PII scrub. Any field that looks like a phone / email / SSN gets
// dropped before we send. Defense-in-depth on top of the caller
// discipline.
const PII_PATTERNS = [
  /\b\d{3}[-.\s]?\d{3}[-.\s]?\d{4}\b/,          // phone
  /\b[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}\b/i, // email
  /\b\d{3}-?\d{2}-?\d{4}\b/,                    // SSN
];
function looksLikePII(v) {
  if (typeof v !== 'string') return false;
  return PII_PATTERNS.some(re => re.test(v));
}
function scrubShape(shape) {
  const out = {};
  for (const [k, v] of Object.entries(shape || {})) {
    if (Array.isArray(v)) {
      out[k] = v.slice(0, 20).map(x => (typeof x === 'string' && looksLikePII(x)) ? '[redacted]' : x);
    } else if (typeof v === 'string') {
      out[k] = looksLikePII(v) ? '[redacted]' : v.slice(0, 400);
    } else {
      out[k] = v;
    }
  }
  return out;
}

/**
 * Signal the admin panel about a subscriber problem.
 *
 * @param {Object}  args
 * @param {string}  args.tag        e.g. 'lead_csv_import' | 'statement_parse' | 'onboarding_stall'
 * @param {string}  [args.owner_id] Supabase user_id if available.
 * @param {string}  [args.email]    Subscriber email if available.
 * @param {Object}  args.shape      Shape hints ONLY. Column headers, file type,
 *                                   error string, byte count, row count. NEVER raw rows.
 * @param {string}  [args.severity] 'info' | 'warn' | 'error' (default 'warn')
 * @returns {void}  Fire-and-forget. Never throws.
 */
export function askAdminForHelp(args = {}) {
  try {
    const { tag, owner_id, email, shape, severity } = args;
    if (!tag) return;

    const cleanShape = scrubShape(shape || {});
    const key = dedupeKey({ tag, owner_id, shape: cleanShape });
    if (!shouldSend(key)) return;
    markSent(key);

    const request_id = makeRequestId();
    const body = JSON.stringify({
      request_id,
      tag,
      owner_id: owner_id || null,
      email:    email    || null,
      severity: severity || 'warn',
      shape:    cleanShape,
      client_ts: new Date().toISOString(),
    });

    const controller = new AbortController();
    const t = setTimeout(() => controller.abort(), TIMEOUT_MS);

    // Server-side proxy hides the shared secret from the browser.
    fetch('/api/subscriber-help/signal', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      signal:  controller.signal,
      keepalive: true, // survives page-nav / tab-close
    })
      .then(() => {}, () => {})
      .finally(() => clearTimeout(t));
  } catch { /* never surface — this is fire-and-forget */ }
}
